// Invite-token persistence across OAuth round-trips.
//
// The OAuth handshake (Google One Tap, GitHub authorize redirect) strips
// arbitrary `?token=<inviteToken>` query params on the way to / back from
// the provider. We stash the invite token in localStorage under a
// per-provider key on the way out, then read+clear it on the callback
// page so the SDK's googleAuth/githubAuth handlers can consume it.
//
// FA204 / frank trap — `_key` used to be a bare module-scope helper, read
// from inside each exported function's own body: unresolvable once this
// file's exports are individually frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers). Inlined as a local in each.

// Read the invite token from the current URL's `?token=` param and
// stash under the provider's key. No-op when the URL has no token.
export const stashInviteToken = (provider) => {
  const key = (p) => `${p}_auth_invite_token`
  if (typeof window === 'undefined' || !provider) return null
  try {
    const token = new URL(window.location.href).searchParams.get('token')
    if (token) window.localStorage.setItem(key(provider), token)
    return token || null
  } catch (_) {
    return null
  }
}

// Read + clear in one call — single-use on the callback side.
export const readInviteToken = (provider) => {
  const key = (p) => `${p}_auth_invite_token`
  if (typeof window === 'undefined' || !provider) return null
  try {
    const token = window.localStorage.getItem(key(provider))
    if (token) window.localStorage.removeItem(key(provider))
    return token
  } catch (_) {
    return null
  }
}

// TAKE the invitation token of this page OUT of the address bar, into memory
// (INVITE-MAGIC-LINK-1 hardening, review 2026-10-09).
//
// The token is a bearer credential. A magic link carries it in the fragment
// (`#token=`), an emailed invitation in the query (`?token=`). The FIRST
// script of workspace/index.html takes it out of the address bar on the
// accept paths — before any other script runs or any request leaves — and
// parks it on a read-once, non-enumerable `window.__symbolsInviteToken`.
// This reads that, once. A page reached by an IN-APP navigation (the router,
// after sign-in) had no boot step: then this reads the address bar itself
// (query or fragment) and takes the token out the same way. The address bar
// wins over the parked token: it is the newer of the two.
//
// The caller keeps the token in memory (a local), never in storage or the
// URL. Returns the token or null. Never throws. Self-contained (FA204).
export const takeInviteToken = () => {
  if (typeof window === 'undefined') return null
  let parked = null
  try {
    // The boot getter deletes itself on read; a plain value is dropped here.
    parked = window.__symbolsInviteToken || null
    if ('__symbolsInviteToken' in window) delete window.__symbolsInviteToken
  } catch (_) {
    parked = null
  }
  let fromUrl = null
  try {
    const url = new URL(window.location.href)
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''))
    fromUrl = url.searchParams.get('token') || hash.get('token') || null
    if (fromUrl) {
      url.searchParams.delete('token')
      hash.delete('token')
      const rest = hash.toString()
      const history = window.history
      if (history && typeof history.replaceState === 'function') {
        history.replaceState(
          history.state,
          '',
          url.pathname + url.search + (rest ? '#' + rest : '')
        )
      }
    }
  } catch (_) {
    /* a malformed address: nothing to take out */
  }
  return fromUrl || parked
}

// The claims an invitation link carries, for COPY and ROUTING only
// (INVITE-MAGIC-LINK-1): `el.call('inviteTokenClaims', token)`.
//
// The link's `?token=` is the server's signed invitation JWT. Its payload is
// base64url — readable, not secret — and it is NOT verified here: the server
// verifies it on every use. So nothing read from it may ever decide access;
// the pages use it to know whether the invite names an email (an emailed
// invitation does, a magic link does not) and which org it is for.
//
// Returns `{ type, email, organizationId, kind, role }` (an absent claim is
// null), or null for a malformed token. Never throws. Self-contained (FA204):
// no module-scope helper.
export const inviteTokenClaims = (token) => {
  if (!token || typeof token !== 'string') return null
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
    const binary = atob(b64 + pad)
    // atob yields one char per byte; the payload is UTF-8 (an address may
    // carry a non-ASCII part).
    const json = decodeURIComponent(
      Array.from(binary, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')
    )
    const p = JSON.parse(json)
    if (!p || typeof p !== 'object') return null
    return {
      type: typeof p.type === 'string' ? p.type : null,
      email: typeof p.email === 'string' && p.email ? p.email : null,
      organizationId: p.organizationId ? String(p.organizationId) : null,
      kind: typeof p.kind === 'string' ? p.kind : null,
      role: typeof p.role === 'string' ? p.role : null
    }
  } catch (_) {
    return null
  }
}

// The invitation token a `?next=` return path carries (SIGNUP-INVITE-RELOAD,
// 2026-10-14). The accept page sends a signed-out invitee to
// `/signup?token=<t>&next=<accept path>#token=<t>`; /signup takes `?token=`
// out of the address bar on mount, so a RELOAD of /signup lost the invite —
// the heading fell back to the generic one and the invited email unlocked.
// The token still rides in `next` (in its FRAGMENT, by the accept page's own
// design), so the context is rebuilt from there — no storage, nothing new in
// the URL. Only a same-origin RELATIVE path to one of the accept routes
// counts; anything else answers null. `search` defaults to the live address
// bar. Never throws. Self-contained (FA204): no module-scope helper.
export const inviteTokenFromNext = (search) => {
  try {
    const q =
      typeof search === 'string'
        ? search
        : typeof window !== 'undefined' && window.location
          ? window.location.search
          : ''
    const next = new URLSearchParams(q || '').get('next')
    if (!next || next.charAt(0) !== '/' || next.charAt(1) === '/') return null
    const url = new URL(next, 'https://local.invalid')
    if (url.origin !== 'https://local.invalid') return null
    const path = url.pathname.replace(/\/+$/, '')
    const acceptPaths = [
      '/accept-invite',
      '/accept-org-invite',
      '/accept-team-invite',
      '/accept-workspace-invite'
    ]
    if (acceptPaths.indexOf(path) === -1) return null
    const hash = new URLSearchParams(url.hash.replace(/^#/, ''))
    return hash.get('token') || url.searchParams.get('token') || null
  } catch (_) {
    return null
  }
}

// Drop the stash without consuming — for error paths that need to clean up.
export const clearInviteToken = (provider) => {
  const key = (p) => `${p}_auth_invite_token`
  if (typeof window === 'undefined' || !provider) return
  try {
    window.localStorage.removeItem(key(provider))
  } catch (_) {}
}
